'use strict';

bbw.model.Conversation = ngtk.model.Base.extend({

    statics: {
        LOAD_FROM_HISTORY_CHUNK_SIZE: 50
    },

    _data: null,
    _serialMap: null,
    _photosCache: null,
    _contactPrivatePhotosLoader: null,
    _userPrivatePhotoAlbumLoader: null,
    _historyPhotoSize: null,
    _errorMap: null,
    _loading: false,

    _initialize: function() {
        this._data = {
            contact: {},
            messages: [],
            unread_messages_count: 0,
            has_older_messages: false
        };
        this._serialMap = {};
        this._photosCache = {};
        this._errorMap = {};
        this._loading = true;
    },

    isLoading: function() {
        if (this.error) {
            return false;
        }
        return this._loading;
    },

    updateUnreadMessagesCount: function(count) {
        count = parseInt(count);
        if (isNaN(count)) {
            count = 0;
        }
        this._data.unread_messages_count = count;
    },

    getContact: function() {
        return this._data.contact;
    },

    getMessages: function() {
        return this._data.messages;
    },

    loadPhotos: function(conversationMessage) {
        if (conversationMessage.hasPhotosLoaded() || !conversationMessage.canLoadPhotos()) {
            return this.$q.when(true);
        }

        var photo_ids = conversationMessage.photo_ids;
        var not_cached_photo_ids = photo_ids.reduce(function(carry, photo_id) {
            if (angular.isUndefined(this._photosCache[photo_id])) {
                carry.push(photo_id);
            }
            return carry;
        }.bind(this), []);

        var loader;
        if (not_cached_photo_ids.length) {
            if (conversationMessage.isIncoming()) {
                loader = this._loadContactPrivatePhotosToCache.bind(this, not_cached_photo_ids);
            } else {
                loader = this._loadUserPrivateAlbumPhotosToCache.bind(this);
            }
        } else {
            loader = this.$q.when.bind(this.$q, true);
        }

        return loader().then(function() {
            var photos = [];
            photo_ids.forEach(function(photo_id) {
                var photo = this._photosCache[photo_id];
                if (photo) {
                    photos.push(photo);
                }
            }.bind(this));
            conversationMessage.setPhotos(photos);
        }.bind(this));
    },

    _loadUserPrivateAlbumPhotosToCache: function() {
        if (!angular.isFunction(this._userPrivatePhotoAlbumLoader)) {
            throw new Error('User private photoalbum loader has not been set');
        }
        return this._userPrivatePhotoAlbumLoader().then(function(privateAlbum) {
            privateAlbum.forEach(this._addPhotoToCache.bind(this));
        }.bind(this));
    },

    _loadContactPrivatePhotosToCache: function(photo_ids) {
        if (!photo_ids.length) {
            return this.$q.when(true);
        }
        if (!angular.isFunction(this._contactPrivatePhotosLoader)) {
            throw new Error('Contacts private photos loader has not been set');
        }
        return this._contactPrivatePhotosLoader(this.getContactUserId(), photo_ids).
            then(function(photosCollection) {
                photosCollection.forEach(this._addPhotoToCache.bind(this));
            }.bind(this)).
            catch(function(error) {
                console.error(error);
            }.bind(this));
    },

    _addPhotoToCache: function(photo) {
        var photo_id = photo.getId();
        if (!photo_id) {
            console.error("Malformed response: missing id in photo", photo);
        } else {
            this._photosCache[photo_id] = photo.get();
        }
    },

    getUnreadMessagesCount: function() {
        return this._data.unread_messages_count;
    },

    hasOlderMessages: function() {
        return this._data.has_older_messages;
    },

    getContactUserId: function() {
        var contact = this._data.contact;
        if (angular.isUndefined(contact)) {
            throw new Error('setUser() must be called first')
        }
        return contact.user_id;
    },

    setContact: function(contact) {
        if (!angular.isObject(contact) || !contact.user_id) {
            throw new Error('invalid_argument');
        }
        this._data.contact = contact;
        return this;
    },

    setContactPrivatePhotosLoader: function(photoLoaderCb) {
        this._contactPrivatePhotosLoader = angular.isFunction(photoLoaderCb) ? photoLoaderCb : null;
        return this;
    },

    setUserPrivatePhotoAlbumLoader: function(privateAlbumLoader) {
        this._userPrivatePhotoAlbumLoader = angular.isFunction(privateAlbumLoader) ? privateAlbumLoader : null;
        return this;
    },

    setHistoryPhotoSize: function(size) {
        this._historyPhotoSize = size;
        return this;
    },

    initialize: function() {
        this._loading = true;
        this._data.messages = [];
        return this.loadFromHistory().finally(function() {
            this._loading = false;
        }.bind(this));
    },

    loadMoreFromHistory: function() {
        if (!this.hasOlderMessages()) {
            return this.$q.when(true);
        }
        this._data.loading_more = true;
        return this.loadFromHistory().finally(function() {
            delete this._data.loading_more;
        }.bind(this));
    },

    isLoadingMore: function() {
        return this._data.loading_more;
    },

    loadFromHistory: function(user_id) {
        try {
            var contact_user_id  = this.getContact().user_id;
        } catch (e) {
            throw new Error('Contact has not been defined');
        }

        if (user_id) {
            this._context_user_id = user_id;
        } else {
            user_id = this._context_user_id;
        }

        var beforeSerial = 0;
        if (this._data.messages.length) {
            beforeSerial = this._data.messages[0].serial || 0;
        }

        var apiQueryTmpl = (user_id
                    ? 'admin/chat/get-chat-history/{user_id}'
                    : 'chat/get-history'
            ) + '/{contact_user_id}/{before_serial}/{limit}/{size_name}';

        return this._loadFromApi('get', apiQueryTmpl, {
            user_id: user_id,
            contact_user_id: contact_user_id,
            before_serial: beforeSerial,
            limit: bbw.model.Conversation.LOAD_FROM_HISTORY_CHUNK_SIZE,
            size_name: this._historyPhotoSize || ''
        }).then(function(response) {
            this.error = null;
            if (response) {
                this._data.has_older_messages = !!response.has_older_messages;
            }
            if (response && angular.isArray(response.history) && response.history.length) {
                var prependMessages = [];
                response.history.forEach(function(row) {
                    var message = bbw.model.ConversationMessage.createFromHistoryRow(row, contact_user_id);
                    this._validateMessage(message);
                    var exists = this._serialMap[message.serial];
                    if (exists) {
                        angular.extend(this._serialMap[message.serial], message);
                    } else {
                        this._serialMap[message.serial] = message;
                        prependMessages.push(message);
                    }
                }.bind(this));
                if (prependMessages.length) {
                    prependMessages.unshift(0, 0);
                    Array.prototype.splice.apply(this._data.messages, prependMessages);
                }
            }
            this._loading = false;
        }.bind(this));
    },

    addMessage: function(message) {
        this._validateMessage(message);
        var exists = this._serialMap[message.serial];
        if (exists) {
            angular.extend(message, this._serialMap[message.serial]);
        }
        this._serialMap[message.serial] = message;
        if (!exists) {
            this._data.messages.push(message);
        }
        if (this._errorMap[message.serial]) {
            var error = this._errorMap[message.serial];
            delete this._errorMap[message.serial];
            this._serialMap[message.serial].setError(error);
        }
        this.loadPhotos(this._serialMap[message.serial]);
        return message;
    },

    markSent: function(serial) {
        if (!serial) {
            return;
        }
        var message = this._findMessageBySerial(serial);
        if (message) {
            message.status = bbw.model.ConversationMessage.STATUS_SENT;
        }
    },

    markSendError: function(serial, error) {
        var message = this._findMessageBySerial(serial);
        if (message) {
            message.setError(error);
        } else {
            this._rememberError(serial, error);
        }
    },

    _rememberError: function(serial, error) {
        this._errorMap[serial] = error;
    },

    _validateMessage: function(message) {
        if (!(message instanceof bbw.model.ConversationMessage)) {
            throw new Error("Invalid argument: not an instance of ConversationMessage");
        }
        if (angular.isUndefined(message.serial)) {
            throw new Error("Invalid argument: message.serial is undefined");
        }
        return message;
    },

    _findMessageBySerial: function(serial) {
        return this._serialMap[serial];
    },

    markRead: function(serial) {
        var isChanged = false;
        this._data.messages.forEach(function(message) {
            if (message.isOutgoing() && (!serial || message.serial <= serial) && message.status !== bbw.model.ConversationMessage.STATUS_READ) {
                message.setRead();
                isChanged = true;
            }
        });
        return isChanged;
    },

    getLastIncomingSerial: function() {
        var result = 0;
        this._data.messages.forEach(function(message) {
            if (message.isIncoming() && message.serial > result) {
                result = message.serial;
            }
        });
        return result;
    }

});